import React, { useState } from 'react'
import { Play, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { TestCase } from '../types'

interface TestCasesProps {
  testCases: TestCase[]
  currentCode: string
  selectedLanguage: string
}

interface TestResult {
  testCase: TestCase
  passed: boolean
  output?: string
  error?: string
}

const TestCases: React.FC<TestCasesProps> = ({ testCases, currentCode, selectedLanguage }) => {
  const [results, setResults] = useState<TestResult[]>([])
  const [isRunning, setIsRunning] = useState(false)

  const runTests = async () => {
    setIsRunning(true)
    setResults([])

    // Simulate test execution (in a real app, this would call a backend API)
    const newResults: TestResult[] = []
    
    for (const testCase of testCases) {
      // Simulate test execution delay
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Mock test result (random pass/fail for demo)
      const passed = Math.random() > 0.3 // 70% pass rate for demo
      const result: TestResult = {
        testCase,
        passed,
        output: passed ? testCase.expectedOutput : 'Wrong output',
        error: passed ? undefined : 'Test failed'
      }
      newResults.push(result)
    }
    
    setResults(newResults)
    setIsRunning(false)
  }

  const getStarterCodeForLanguage = (language: string) => {
    const codeBlocks = currentCode.split('# JavaScript')
    if (language === 'python' && codeBlocks[0]) {
      return codeBlocks[0].replace('# Python\n', '').trim()
    } else if (language === 'javascript' && codeBlocks[1]) {
      return codeBlocks[1].replace('# JavaScript\n', '').trim()
    }
    return currentCode
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Test Cases</h3>
        <button
          onClick={runTests}
          disabled={isRunning}
          className="flex items-center space-x-2 btn-primary"
        >
          {isRunning ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Play className="h-4 w-4" />
          )}
          <span>{isRunning ? 'Running...' : 'Run Tests'}</span>
        </button>
      </div>

      <div className="space-y-3">
        {testCases.map((testCase, index) => {
          const result = results[index]
          return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Test Case {index + 1}
                    {testCase.description && (
                      <span className="text-sm text-gray-500 ml-2">- {testCase.description}</span>
                    )}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Input:</span>
                      <code className="ml-2 bg-gray-100 px-2 py-1 rounded">{testCase.input}</code>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Expected:</span>
                      <code className="ml-2 bg-gray-100 px-2 py-1 rounded">{testCase.expectedOutput}</code>
                    </div>
                    {result && (
                      <div>
                        <span className="font-medium text-gray-700">Output:</span>
                        <code className={`ml-2 px-2 py-1 rounded ${
                          result.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {result.output}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  {result ? (
                    result.passed ? (
                      <CheckCircle className="h-6 w-6 text-green-500" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-500" />
                    )
                  ) : (
                    <AlertCircle className="h-6 w-6 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {results.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Results: {results.filter(r => r.passed).length}/{results.length} passed
            </span>
            {results.every(r => r.passed) && (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">All tests passed!</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TestCases 